import { useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';
import { TableModal } from '@shared/components/modals/TableModal';
import { IOption } from '@shared/interfaces';
import { SERVICES } from '@shared/constants/apiConfig';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { Wrapper } from '@styles/CommonStyles';
import { IStateTabCurrentData, IStateTabData, IStipTypeData } from '../interfaces';
import { requestCreateState, requestDeleteState, requestUpdateState } from '../requests';
import { stateTabSchema } from './validation';
import { stateTabColumns, stateTabFormConfig } from './constants';

interface IStateTabProps {
  stipTypeOptions: IOption[];
}

const formatForCurrentData = (data: IStipTypeData[]) => {
  return data?.map((stipType) => stipType?.id);
};

export const StateTab = ({ stipTypeOptions }: IStateTabProps) => {
  const { data: stateData, mutate: reloadData } = useSWR<IStateTabData[]>(`/${SERVICES.DOCUMENT}/titleScan/state`, {revalidateOnMount: undefined});
  const [currentData, setCurrentData] = useState<IStateTabCurrentData>();
  const [openModal, setOpenModal] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);

  const handleAddNewRecordBtn = useCallback(async (data: IStateTabData) => {
    setSubmitting(true);
    if (data.id) {
      await requestUpdateState(data);
    } else {
      await requestCreateState(data);
    }
    
    reloadData();
    setOpenModal(false);
    setSubmitting(false);
  }, [reloadData]);

  const handleNewBtn = useCallback(() => {
    setOpenModal(true);
    setCurrentData({} as IStateTabCurrentData);
  }, []);

  const gridActions = {
    onEdit: (data: IStateTabData) => {
      setCurrentData({
        ...data,
        straightRefiBorrower: formatForCurrentData(data?.straightRefiBorrower) || [],
        straightRefiCoBorrower: formatForCurrentData(data?.straightRefiCoBorrower) || [],
        add: formatForCurrentData(data?.add) || [],
        drop: formatForCurrentData(data?.drop) || [],
        addDrop: formatForCurrentData(data?.addDrop) || [],
        nameChangeBorrower: formatForCurrentData(data?.nameChangeBorrower) || [],
        nameChangeCoBorrower: formatForCurrentData(data?.nameChangeCoBorrower) || [],
      });
      setOpenModal(true);
    },
    onDelete: (data: IStateTabData) => {
      requestDeleteState(data?.id || '');
      reloadData();
    },
  };

  const editingData = useMemo<IStateTabData | IStateTabCurrentData>(
    () => ({
      id: currentData?.id || null,
      stateCode: currentData?.stateCode || '',
      straightRefiBorrower: currentData?.straightRefiBorrower || [],
      straightRefiCoBorrower: currentData?.straightRefiCoBorrower || [],
      add: currentData?.add || [],
      drop: currentData?.drop || [],
      addDrop: currentData?.addDrop || [],
      nameChangeBorrower: currentData?.nameChangeBorrower || [],
      nameChangeCoBorrower: currentData?.nameChangeCoBorrower || [],
    }),
    [currentData]
  );

  const stateFormConfig = stateTabFormConfig(stipTypeOptions);

  return (
    <Wrapper>
      <TableModal
        isOpen={!!openModal}
        title="Title Scan State"
        editingData={editingData as IStateTabData}
        formConfig={stateFormConfig}
        validationSchema={stateTabSchema}
        isSubmitting={isSubmitting}
        onSubmit={handleAddNewRecordBtn}
        onClose={() => setOpenModal(false)}
      />
      <PhoenixGrid
        autoHeight
        withHeader
        canNotCopy
        isFullHeightColumn
        buttonTitle="Add New Record"
        columns={stateTabColumns}
        actions={gridActions}
        rows={stateData || []}
		getRowHeight={() => 'auto'}
        loading={!stateData}
        onButtonClick={handleNewBtn}
      />
    </Wrapper>
  );
};
